import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import NativeTextInput from "react-native-web/dist/exports/TextInput";
import View from "react-native-web/dist/exports/View";
import { applyTextStyles, useTheme, useThemeTokens, applyOuterBorder } from '../ThemeProvider';
import StackView from '../StackView';
import IconButton from '../IconButton';
import { a11yProps, getTokensPropType, selectSystemProps, textInputHandlerProps, textInputProps, useCopy, useInputValue, useSpacingScale, variantProp, viewProps } from '../utils';
import dictionary from './dictionary';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, textInputHandlerProps, textInputProps, viewProps]);

const selectInputStyles = (_ref, themeOptions, inactive) => {
  let {
    backgroundColor,
    color,
    borderWidth = 0,
    borderColor,
    borderRadius,
    paddingTop = 0,
    paddingBottom = 0,
    paddingLeft,
    paddingRight = 0,
    fontName,
    fontSize,
    fontWeight,
    lineHeight,
    icon,
    iconSize = 0,
    minLines,
    maxLines,
    width,
    height
  } = _ref;

  // Subtract border width from padding so overall input width/height doesn't
  // jump around if the border width changes (avoiding NaN and negative padding)
  const offsetBorder = value => typeof value === 'number' ? Math.max(0, value - borderWidth) : value;

  const textStyles = applyTextStyles({
    fontName,
    fontSize,
    lineHeight,
    fontWeight,
    themeOptions
  });

  function linesToHeight(lines) {
    const {
      lineHeight: absoluteLineHeight
    } = textStyles;
    return lines !== undefined ? lines * absoluteLineHeight + paddingTop + paddingBottom : undefined;
  }

  const minHeight = linesToHeight(minLines);
  const maxHeight = linesToHeight(maxLines);
  const webStyles = Platform.select({
    web: {
      outline: 'none',
      cursor: inactive ? 'not-allowed' : undefined,
      resize: minHeight !== maxHeight ? 'vertical' : 'none' // does nothing for an input, only needed for textarea

    }
  });
  const paddingWithIcon = iconSize + paddingRight;
  return {
    backgroundColor,
    color,
    borderWidth,
    borderColor,
    borderRadius,
    paddingLeft: offsetBorder(paddingLeft),
    paddingRight: icon ? offsetBorder(paddingWithIcon) : offsetBorder(paddingRight),
    paddingTop: offsetBorder(paddingTop),
    paddingBottom: offsetBorder(paddingBottom),
    minHeight,
    maxHeight,
    width,
    height,
    ...textStyles,
    ...webStyles
  };
};

const selectOuterBorderStyles = _ref2 => {
  let {
    outerBackgroundColor,
    outerBorderWidth,
    outerBorderColor,
    borderRadius
  } = _ref2;
  return {
    background: outerBackgroundColor,
    ...applyOuterBorder({
      outerBorderColor,
      outerBorderWidth,
      borderRadius
    })
  };
};

const selectIconTokens = _ref3 => {
  let {
    iconSize,
    iconColor
  } = _ref3;
  return {
    size: iconSize,
    color: iconColor
  };
};

const selectIconContainerStyles = (_ref4, buttonCount) => {
  let {
    buttonSize,
    buttonsGapSize,
    paddingRight,
    paddingBottom
  } = _ref4;
  return {
    paddingRight: paddingRight + buttonCount * (buttonSize + buttonsGapSize),
    paddingBottom
  };
};

const selectButtonsContainerStyle = _ref5 => {
  let {
    buttonsPaddingRight
  } = _ref5;
  return {
    paddingRight: buttonsPaddingRight
  };
};

const TextInputBase = /*#__PURE__*/forwardRef((_ref6, ref) => {
  let {
    buttons = [],
    copy = 'en',
    height,
    inactive,
    initialValue,
    onBlur,
    onChange,
    onChangeText,
    onClear,
    onFocus,
    onMouseOut,
    onMouseOver,
    pattern,
    readOnly,
    tokens,
    value,
    variant = {},
    ...rest
  } = _ref6;
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleFocus = event => {
    setIsFocused(true);
    if (typeof onFocus === 'function') onFocus(event);
  };

  const handleBlur = event => {
    setIsFocused(false);
    if (typeof onBlur === 'function') onBlur(event);
  };

  const [isHovered, setIsHovered] = useState(false);

  const handleMouseOver = event => {
    setIsHovered(true);
    if (typeof onMouseOver === 'function') onMouseOver(event);
  };

  const handleMouseOut = event => {
    setIsHovered(false);
    if (typeof onMouseOut === 'function') onMouseOut(event);
  };

  const defaultRef = useRef();
  const inputRef = ref ?? defaultRef;
  const {
    currentValue,
    resetValue,
    setValue,
    isControlled,
    isDirty
  } = useInputValue({
    value,
    initialValue,
    inputRef,
    onChange,
    readOnly
  });
  const element = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
  useEffect(() => {
    if (Platform.OS === 'web' && pattern && element) {
      // React Native Web doesn't support `pattern`, so we have to attach it via a ref,
      // which a `pattern` user must provide anyway to call .checkValidity() on the element.
      element.pattern = pattern;
    }
  }, [element, pattern]);

  const handleChangeText = event => {
    var _event$nativeEvent, _event$target;

    const text = ((_event$nativeEvent = event.nativeEvent) === null || _event$nativeEvent === void 0 ? void 0 : _event$nativeEvent.text) || ((_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value);
    setValue(text, event);
    if (typeof onChangeText === 'function') onChangeText(text, event);
  };

  const states = {
    focus: isFocused,
    hover: isHovered,
    inactive
  };
  const themeTokens = useThemeTokens('TextInput', tokens, variant, states);

  const handleClear = event => {
    var _inputRef$current;

    onClear === null || onClear === void 0 ? void 0 : onClear(event);
    resetValue(event);
    inputRef === null || inputRef === void 0 ? void 0 : (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
  };

  const handleShowOrHide = () => {
    if (!variant.inactive) setShowPassword(!showPassword);
  };

  const {
    buttonsGap,
    clearButtonIcon: ClearButtonIcon,
    icon: IconComponent,
    passwordShowButtonIcon,
    passwordHideButtonIcon
  } = themeTokens;
  const buttonsGapSize = useSpacingScale(buttonsGap);
  const getCopy = useCopy({
    dictionary,
    copy
  });
  const textInputButtons = buttons;

  if (onClear && isDirty) {
    textInputButtons === null || textInputButtons === void 0 ? void 0 : textInputButtons.unshift( /*#__PURE__*/_jsx(IconButton, {
      accessibilityLabel: getCopy('clearButtonAccessibilityLabel'),
      icon: ClearButtonIcon,
      onPress: handleClear,
      variant: {
        compact: true
      }
    }, "clear"));
  }

  if (variant.password) {
    textInputButtons === null || textInputButtons === void 0 ? void 0 : textInputButtons.unshift( /*#__PURE__*/_jsx(IconButton, {
      accessibilityLabel: !showPassword ? getCopy('hidePasswordAccessibilityLabel') : getCopy('showPasswordAccessibilityLabel'),
      icon: !showPassword ? passwordShowButtonIcon : passwordHideButtonIcon,
      onPress: handleShowOrHide,
      variant: {
        compact: true,
        password: true,
        inactive: !!variant.inactive
      }
    }, !showPassword ? 'hide' : 'show'));
  }

  const inputProps = { ...selectProps(rest),
    editable: !inactive,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onMouseOver: handleMouseOver,
    onMouseOut: handleMouseOut,
    onChange: handleChangeText,
    defaultValue: initialValue,
    // currentValue is being updated even if the input is not controlled, passing it down to the
    // Input could lead to changing its state from uncontrolled to controlled
    value: isControlled ? currentValue : undefined
  };
  const {
    themeOptions
  } = useTheme();
  const nativeInputStyle = selectInputStyles({ ...themeTokens,
    height
  }, themeOptions, inactive);
  return /*#__PURE__*/_jsxs(View, {
    style: selectOuterBorderStyles(themeTokens),
    children: [/*#__PURE__*/_jsx(NativeTextInput, {
      ref: inputRef,
      style: nativeInputStyle,
      secureTextEntry: variant.password && !showPassword,
      ...inputProps
    }), IconComponent && /*#__PURE__*/_jsx(View, {
      pointerEvents: "none" // avoid hijacking input press events
      ,
      style: [staticStyles.iconContainer, selectIconContainerStyles({ ...themeTokens,
        buttonsGapSize
      }, buttons === null || buttons === void 0 ? void 0 : buttons.length)],
      children: /*#__PURE__*/_jsx(IconComponent, { ...selectIconTokens(themeTokens)
      })
    }), (buttons === null || buttons === void 0 ? void 0 : buttons.length) > 0 && /*#__PURE__*/_jsx(View, {
      style: [staticStyles.buttonsContainer, selectButtonsContainerStyle(themeTokens)],
      children: /*#__PURE__*/_jsx(StackView, {
        direction: "row",
        space: buttonsGap,
        children: textInputButtons
      })
    })]
  });
});
TextInputBase.displayName = 'TextInputBase';
TextInputBase.propTypes = { ...selectedSystemPropTypes,
  buttons: PropTypes.arrayOf(PropTypes.node),

  /**
   * Select English or French copy for the accessible labels.
   * You may also pass in a custom dictionary object.
   */
  copy: PropTypes.oneOfType([PropTypes.oneOf(['en', 'fr']), PropTypes.shape({
    clearButtonAccessibilityLabel: PropTypes.string
  })]),
  height: PropTypes.number,
  inactive: PropTypes.bool,
  initialValue: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onChangeText: PropTypes.func,
  onClear: PropTypes.func,
  onFocus: PropTypes.func,
  onMouseOut: PropTypes.func,
  onMouseOver: PropTypes.func,
  readOnly: PropTypes.bool,
  tokens: getTokensPropType('TextInput', 'TextArea'),
  value: PropTypes.string,
  variant: variantProp.propType
};
export default TextInputBase;
const staticStyles = StyleSheet.create({
  buttonsContainer: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center'
  },
  iconContainer: {
    position: 'absolute',
    right: 0,
    bottom: 0
  }
});